"use client";

import { AlertCircle, ChevronLeft, ChevronRight, X } from "lucide-react";
import { GalleryViewProps } from "../../types/ui.types";
import { AnimatePresence, motion } from "framer-motion";
import { PALETTE } from "../../lib/colorPallete";
import { getSplitBackground } from "../../utils/getSplitBackground";
import Image from "next/image";

const ChevronLeftIcon = () => <ChevronLeft size={24} />;
const ChevronRightIcon = () => <ChevronRight size={24} />;

export const GalleryView: React.FC<GalleryViewProps> = ({
  product,
  imageList,
  currentImageIndex,
  setCurrentImageIndex,
  selectedColorName,
  handleColorSelect,
  availableColors,
  selectedCustomizations,
  quantity,
  isWholeSaleUser,
}) => (
  <div className="w-full flex flex-col p-4 sm:p-6 bg-black/5 h-full">
    {}
    <motion.div
      layoutId={`image-${product._id}`}
      className="relative w-full aspect-4/3 shrink-0 overflow-hidden"
    >
      <AnimatePresence mode="wait">
        <motion.img
          key={imageList[currentImageIndex]}
          src={imageList[currentImageIndex]}
          alt={`${product.modelName} - Image ${currentImageIndex + 1}`}
          className="object-contain w-full h-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        />
      </AnimatePresence>
      {imageList.length > 1 && (
        <>
          <button
            onClick={() =>
              setCurrentImageIndex(
                (p) => (p - 1 + imageList.length) % imageList.length,
              )
            }
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/20 text-white hover:bg-black/40 transition-colors"
          >
            <ChevronLeftIcon />
          </button>
          <button
            onClick={() =>
              setCurrentImageIndex((p) => (p + 1) % imageList.length)
            }
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/20 text-white hover:bg-black/40 transition-colors"
          >
            <ChevronRightIcon />
          </button>
        </>
      )}
    </motion.div>
    {imageList.length > 1 && (
      <div className="mt-4 w-full overflow-x-auto">
        <div className="flex gap-2 p-1">
          {imageList.map((img, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`relative shrink-0 w-16 h-12 rounded-md overflow-hidden border-2 transition-all ${
                currentImageIndex === index
                  ? "border-amber-500"
                  : "border-transparent hover:border-gray-400"
              }`}
            >
              <Image
                src={img}
                alt={`Thumbnail ${index + 1}`}
                height={500}
                width={500}
              />
            </button>
          ))}
        </div>
      </div>
    )}

    {}
    {availableColors && availableColors.length > 0 && (
      <>
        {}
        {!isWholeSaleUser || quantity >= 300 ? (
          <div className="mt-auto pt-6">
            <h4
              className="font-semibold mb-3"
              style={{ color: PALETTE.darkGreen }}
            >
              Available Colors
            </h4>
            <div className="flex flex-wrap gap-3">
              <AnimatePresence>
                {availableColors.map((color) => {
                  const currentSelections = Object.values(
                    selectedCustomizations,
                  );
                  const isOutOfStock =
                    Array.isArray(color.inStockFor) &&
                    !currentSelections.some((selectedValue: string) =>
                      color.inStockFor.includes(selectedValue),
                    );

                  return (
                    <motion.button
                      key={color.name}
                      layoutId={color.name}
                      layout
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{
                        opacity: 1,
                        scale: selectedColorName === color.name ? 1.15 : 1,
                        boxShadow:
                          selectedColorName === color.name
                            ? "0 0 15px rgba(0,0,0,0.3)"
                            : "0 0 0 rgba(0,0,0,0)",
                      }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{
                        type: "spring",
                        stiffness: 250,
                        damping: 20,
                      }}
                      onClick={() =>
                        !isOutOfStock && handleColorSelect(color.name)
                      }
                      disabled={isOutOfStock}
                      className={`relative w-12 h-12 rounded-full ${
                        isOutOfStock ? "cursor-not-allowed" : "cursor-pointer"
                      }`}
                      style={{
                        background: getSplitBackground(color),
                        border:
                          selectedColorName === color.name
                            ? "3px solid white"
                            : isOutOfStock
                              ? `2px solid ${PALETTE.errorRed}`
                              : "2px solid transparent",
                        opacity: isOutOfStock ? 0.4 : 1,
                        filter: isOutOfStock ? "grayscale(40%)" : "none",
                      }}
                      title={`${color.name}${
                        isOutOfStock
                          ? " (Out of Stock for current selection)"
                          : ""
                      }`}
                    >
                      {isOutOfStock && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-full">
                          <X size={24} className="text-red-400 font-bold" />
                        </div>
                      )}
                      {selectedColorName === color.name && !isOutOfStock && (
                        <motion.div
                          layoutId="color-dot"
                          className="absolute inset-0 flex items-center justify-center"
                          initial={false}
                          animate={{ scale: 1 }}
                          transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 25,
                          }}
                        ></motion.div>
                      )}
                    </motion.button>
                  );
                })}
              </AnimatePresence>
            </div>

            <div
              className="flex items-center gap-2 mt-3 text-sm text-yellow-900 border-2 rounded-full p-2"
              style={{ backgroundColor: `${PALETTE.gold}80` }}
            >
              <AlertCircle size={16} />
              <span>Colors are subject to availability.</span>
            </div>
          </div>
        ) : (
          <div className="mt-auto pt-6">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="p-4 rounded-md bg-amber-50 border border-amber-200"
            >
              <h4
                className="font-semibold mb-1"
                style={{ color: PALETTE.darkGreen }}
              >
                Color Customization
              </h4>
              <p
                className="text-sm"
                style={{ color: PALETTE.darkGreen, opacity: 0.8 }}
              >
                Color selection is available for wholesale orders of 300 units
                or more.
              </p>
            </motion.div>
          </div>
        )}
      </>
    )}
  </div>
);
